import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from './ThemeContext';
import { ExternalLink } from 'lucide-react';

const projects = [
  {
    id: 1,
    title: 'GameCoder',
    description: 'AI-assisted game creation platform built for real classroom use. It helps students plan, build, and iterate on games through guided workflows instead of one-shot generation.',
    tags: ['AI systems', 'TypeScript', 'education', 'agent workflows'],
    image: null,
    link: 'https://gamecoder.org',
    cta: 'Open Live Site',
  },
  {
    id: 2,
    title: 'StudentSafeChat',
    description: 'Teacher-controlled AI chat system designed for safe classroom deployment, with oversight, visibility, and constraint-driven workflows built into the product from the start.',
    tags: ['AI systems', 'AI safety', 'full-stack', 'education'],
    image: null,
    link: 'https://studentsafechat.com',
    cta: 'Open Live Site',
  },
  {
    id: 3,
    title: 'Image Explorer',
    description: 'Recently shipped classroom image exploration tool deployed on Vercel. It gives students a focused interface for working with visual material in a lightweight web app.',
    tags: ['React', 'Vercel', 'education', 'visual tools'],
    image: null,
    link: 'https://image-explorer-classroom.vercel.app',
    cta: 'Open Live Demo',
  },
  {
    id: 4,
    title: 'Wolf Windows',
    description: 'Production website for a Bay Area exterior cleaning company, built to clearly present services and drive quote requests through a straightforward customer-focused experience.',
    tags: ['Next.js', 'frontend', 'client work', 'small business'],
    image: '/images/wolf.png',
    link: 'https://wolfwindowsbayarea.com',
    cta: 'Open Client Site',
  },
  {
    id: 5,
    title: 'Seal Tech Pavement Co.',
    description: 'Client website for a pavement company focused on service clarity, lead generation, and a practical presentation of offerings for real customers.',
    tags: ['React', 'frontend', 'client work', 'lead generation'],
    image: '/images/SealTech.png',
    link: 'https://sealtechpavement.com',
    cta: 'Open Client Site',
  },
  {
    id: 6,
    title: 'Phonics Quest',
    description: 'In-progress K-2 literacy practice product for classroom centers. The MVP focuses on fast student login, teacher-assigned phonics packs, short activities, and trustworthy progress tracking.',
    tags: ['Next.js', 'TypeScript', 'Supabase', 'education'],
    image: '/images/phonics-quest.png',
    status: 'In Progress',
  },
  {
    id: 7,
    title: 'Search Engine',
    description: 'Java search engine project built with an inverted index, multithreaded processing, and a web crawler to improve query performance and retrieval quality.',
    tags: ['Java', 'search', 'multithreading', 'web crawler'],
    image: '/images/search.png',
    link: 'https://github.com/ahmaadidrees/searchEngine/tree/main/src',
    cta: 'View Code',
  },
  {
    id: 8,
    title: 'Blockchain',
    description: 'Go project focused on blockchain fundamentals, including custom block structures, proof of work, and Nakamoto-style consensus validation.',
    tags: ['Go', 'blockchain', 'distributed systems', 'consensus'],
    image: '/images/bitcoin.png',
    link: 'https://github.com/ahmaadidrees/simpleBlockchain/tree/main/P4/data',
    cta: 'View Code',
  },
  {
    id: 9,
    title: 'MaadPod',
    description: 'Custom React Native MP3 player and downloader built around search, retrieval, and local playback workflows across YouTube and SoundCloud sources.',
    tags: ['React Native', 'Node.js', 'mobile', 'media'],
    image: '/images/MaadPod2.png',
    link: 'https://github.com/ahmaadidrees/MaadPod',
    cta: 'View Code',
  },
];

const Projects = () => {
  const { isDarkMode } = useTheme();

  return (
    <motion.div
      className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.h1
         style={{marginTop: 90}}
          className="text-5xl font-bold mb-12 text-center"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          Projects
        </motion.h1>

        <motion.p
          className={`max-w-3xl mx-auto text-center mb-12 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          A focused selection of AI products, client work, and technical projects. The strongest classroom and product work appears first, followed by supporting client and engineering work.
        </motion.p>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
        >
          {projects.map((project) => (
            <motion.div
              key={project.id}
              className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 flex flex-col`}
              variants={{
                hidden: { opacity: 0, y: 50 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
              }}
            >
              {project.image ? (
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center p-6">
                  <h3 className="text-2xl font-semibold text-white text-center">{project.title}</h3>
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 flex-1`}>{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {project.tags.map((tag) => (
                    <span key={tag} className="px-2 py-1 bg-teal-500 text-white text-sm rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                {project.link ? (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-teal-500 hover:text-teal-600 transition-colors duration-200"
                  >
                    {project.cta} <ExternalLink size={16} className="ml-1" />
                  </a>
                ) : (
                  <span className="inline-flex items-center text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {project.status}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Projects;
